import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { AuthService } from '../shared/auth/auth.service';
import { RemoteDoctorService, ActiveHospitalOption } from './services/remote-doctor.service';

@Component({
  selector: 'app-hospital-switcher',
  standalone: true,
  imports: [CommonModule, MatMenuModule, MatIconModule, MatButtonModule, MatSnackBarModule],
  templateUrl: './hospital-switcher.component.html',
})
export class HospitalSwitcherComponent implements OnInit, OnDestroy {
  private auth = inject(AuthService);
  private remoteDoctorService = inject(RemoteDoctorService);
  private snackBar = inject(MatSnackBar);

  isGlobalRemoteDoctor = false;
  myHospitals: ActiveHospitalOption[] = [];
  isLoadingHospitals = false;
  isSwitchingHospital = false;
  hospitalLoadError = false;

  private userSub?: Subscription;

  ngOnInit(): void {
    this.userSub = this.auth.user.subscribe(user => {
      if (!user) {
        this.isGlobalRemoteDoctor = false;
        this.myHospitals = [];
        return;
      }

      const roles = user.roles || [];
      this.isGlobalRemoteDoctor = roles.includes('remotedoctor') && !!(user as any)?.isGlobal;

      if (!this.isGlobalRemoteDoctor) {
        this.myHospitals = [];
      }
    });
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
  }

  get canSwitchHospitals(): boolean {
    return this.isGlobalRemoteDoctor;
  }

  /** Refresh the list every time the submenu is opened, so it's never stale. */
  onMenuOpened(): void {
    this.loadMyHospitals();
  }

  loadMyHospitals(): void {
    this.isLoadingHospitals = true;
    this.hospitalLoadError = false;

    this.remoteDoctorService.getMyActiveHospitals().subscribe({
      next: (res) => {
        this.myHospitals = res.hospitals;
        this.isLoadingHospitals = false;
      },
      error: (err) => {
        this.isLoadingHospitals = false;
        this.hospitalLoadError = true;
        this.myHospitals = [];
        console.error('Failed to load hospitals for switcher', err);
      },
    });
  }

  switchHospital(hospital: ActiveHospitalOption): void {
    if (!hospital || hospital.isCurrent || this.isSwitchingHospital) {
      return;
    }

    this.isSwitchingHospital = true;

    this.remoteDoctorService.switchHospital(hospital.hospitalFhirId).subscribe({
      next: (res) => {
        this.isSwitchingHospital = false;
        this.auth.applySwitchedHospitalSession(
          res.accessToken,
          res.refreshToken,
          res.activeHospitalFhirId,
          res.activeHospitalName ?? undefined
        );
        this.snackBar.open(res.message ?? 'Hospital switched.', 'Close', { duration: 3000 });
        this.loadMyHospitals();
      },
      error: (err) => {
        this.isSwitchingHospital = false;
        const message =
          err?.error?.message ??
          (err?.status === 0
            ? 'Network error — check your connection and try again.'
            : 'Could not switch hospital. Please try again.');
        this.snackBar.open(message, 'Close', { duration: 5000 });
        console.error('Hospital switch failed', err);
      },
    });
  }
}